/* eslint-disable react/sort-comp */
/* eslint-disable no-restricted-syntax */
import React from 'react';
import { withTranslation } from 'react-i18next';
import produce from 'immer';
import { Cross, FileText, InfoCircle, Plus, Trash } from '@saleshandy/icons';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { validate } from './validator';
import Select from '../../../../../../shared/design-system/components/select';
import Input from '../../../../../../shared/design-system/components/input';
import Modal from '../../../../../../shared/design-system/components/atoms/modal';

import {
  CreateUpdateCustomFieldModalProps,
  CreateUpdateCustomFieldModalState,
  CurrencyOption,
  DropdownCreateUpdateOption,
} from './types';
import { FieldType } from '../../../../enums/field';
import { CreateUpdateModalData } from '../../types';
import { accessibleOnClick, capitalize } from '../../../../../../shared/utils';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../../shared/design-system/components/overlay';
import { Images } from '../../../../../../shared/app-constants';
import InputLabel from '../../../../../../shared/design-system/components/input/input-label';
import { PRESET_COLORS } from '../../../../../prospect/helpers/draggable-input/helper';
import SortableColumnField from '../../../../../prospect/helpers/draggable-input/draggable-input';
import { DotsVerticals } from '../../../../../../shared/svg';

type ColumnItem = {
  id: string;
  value: string;
  color: string;
  isHidden?: boolean;
  isNew?: boolean;
  borderColor?: string;
};

type PaletteColor = {
  fill: string;
  border: string;
};

const DUPLICATE_ERROR_MESSAGE = 'A column with this name already exists.';
const DEFAULT_FALLBACK_COLOR = PRESET_COLORS[0];

const options: DropdownCreateUpdateOption[] = [
  { name: FieldType.Text, key: FieldType.Text },
  { name: FieldType.Number, key: FieldType.Number },
  { name: FieldType.LongText, key: FieldType.LongText },
  { name: FieldType.Date, key: FieldType.Date },
  { name: FieldType.Dropdown, key: FieldType.Dropdown },
  { name: FieldType.Currency, key: FieldType.Currency },
];

const currencyOptions = [
  { name: 'US Dollar (USD)', key: 'USD' },
  { name: 'Euro (EUR)', key: 'EUR' },
  { name: 'British Pound Sterling (GBP)', key: 'GBP' },
  { name: 'Japanese Yen (JPY)', key: 'JPY' },
  { name: 'Swiss Franc (CHF)', key: 'CHF' },
  { name: 'Canadian Dollar (CAD)', key: 'CAD' },
  { name: 'Australian Dollar (AUD)', key: 'AUD' },
  { name: 'Chinese Yuan Renminbi (CNY)', key: 'CNY' },
  { name: 'Indian Rupee (INR)', key: 'INR' },
  { name: 'Singapore Dollar (SGD)', key: 'SGD' },
  { name: 'Hong Kong Dollar (HKD)', key: 'HKD' },
  { name: 'New Zealand Dollar (NZD)', key: 'NZD' },
  { name: 'South Korean Won (KRW)', key: 'KRW' },
  { name: 'Swedish Krona (SEK)', key: 'SEK' },
  { name: 'United Arab Emirates Dirham (AED)', key: 'AED' },
  { name: 'Brazilian Real (BRL)', key: 'BRL' },
  { name: 'Mexican Peso (MXN)', key: 'MXN' },
  { name: 'South African Rand (ZAR)', key: 'ZAR' },
  { name: 'Saudi Riyal (SAR)', key: 'SAR' },
];

class CreateUpdateCustomFieldModal extends React.Component<
  CreateUpdateCustomFieldModalProps,
  CreateUpdateCustomFieldModalState
> {
  constructor(props) {
    super(props);

    const { customField } = this.props;

    const initialOptions = customField ? customField.options : [];
    const dropdownOptions = this.convertOptionsToColumnItems(initialOptions);
    const fieldType = customField ? customField.fieldType : FieldType.Text;

    // Initialize currencyCode to first option if field type is Currency and no currencyCode is set
    const initialCurrencyCode =
      fieldType === FieldType.Currency
        ? customField?.currencyCode ?? currencyOptions[0]?.key
        : customField?.currencyCode;

    this.state = {
      values: {
        fieldName: customField ? customField.label : '',
        fallbackText: customField?.fallbackText ? customField.fallbackText : '',
      },
      errors: {
        fieldName: '',
        fallbackText: '',
      },
      dirty: {
        fieldName: false,
        fallbackText: false,
      },
      fieldType,
      isDefault: customField && customField.isDefault,
      currencyCode: initialCurrencyCode,
      options: initialOptions,
      isFallbackHasError: false,
      dropdownOptions,
      activeColorPickerId: null,
      fieldErrors: {},
      newlyAddedColumnId: null,
    };

    this.onInputChange = this.onInputChange.bind(this);
    this.onTextAreaChange = this.onTextAreaChange.bind(this);
    this.onInputBlur = this.onInputBlur.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onSelectChange = this.onSelectChange.bind(this);
    this.createColumnId = this.createColumnId.bind(this);
    this.createColumn = this.createColumn.bind(this);
    this.computeDuplicateErrors = this.computeDuplicateErrors.bind(this);
    this.handleAddColumn = this.handleAddColumn.bind(this);
    this.handleRemoveColumn = this.handleRemoveColumn.bind(this);
    this.handleColumnChange = this.handleColumnChange.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
    this.handleToggleColorPicker = this.handleToggleColorPicker.bind(this);
    this.handleColorChange = this.handleColorChange.bind(this);
  }

  convertOptionsToColumnItems(options: string[]): ColumnItem[] {
    if (!options || options.length === 0) {
      return [this.createColumn('')];
    }
    return options.map((opt) => this.createColumn(opt));
  }

  // eslint-disable-next-line class-methods-use-this
  convertColumnItemsToOptions(columnItems: ColumnItem[]): string[] {
    return columnItems
      .filter((column) => !column.isHidden)
      .map((column) => column.value)
      .filter((value) => value.trim() !== '');
  }

  // eslint-disable-next-line class-methods-use-this
  computeDuplicateErrors(items: ColumnItem[], targetId?: string) {
    if (targetId) {
      const targetColumn = items?.find((column) => column?.id === targetId);

      if (targetColumn?.isHidden) {
        return {};
      }

      if (targetColumn) {
        const trimmedValue = targetColumn?.value?.trim()?.toLowerCase() ?? '';

        if (trimmedValue) {
          const hasDuplicate = items?.some(
            (column) =>
              !column.isHidden &&
              column?.id !== targetId &&
              column?.value?.trim()?.toLowerCase() === trimmedValue,
          );

          if (hasDuplicate) {
            return {
              [targetId]: DUPLICATE_ERROR_MESSAGE,
            };
          }
        }
      }

      return {};
    }

    const valueMap = new Map<string, string[]>();

    items
      .filter((column) => !column.isHidden)
      .forEach(({ id, value }) => {
        const trimmedValue = value?.trim()?.toLowerCase();

        if (!trimmedValue) {
          return;
        }

        const ids = valueMap.get(trimmedValue) ?? [];
        ids.push(id);
        valueMap.set(trimmedValue, ids);
      });

    const duplicates: Record<string, string> = {};

    valueMap.forEach((ids) => {
      if (ids.length > 1) {
        const latestId = ids[ids.length - 1];
        duplicates[latestId] = DUPLICATE_ERROR_MESSAGE;
      }
    });

    return duplicates;
  }

  handleAddColumn() {
    this.setState((prevState) => {
      const newColumn = this.createColumn('');
      const updatedColumns = [...prevState.dropdownOptions, newColumn];
      const fieldErrors = this.computeDuplicateErrors(updatedColumns);
      return {
        dropdownOptions: updatedColumns,
        fieldErrors,
        activeColorPickerId: null,
        newlyAddedColumnId: newColumn.id,
      };
    });
  }

  handleRemoveColumn(id: string) {
    const { customField, onDeleteOptionClick } = this.props;
    const { dropdownOptions } = this.state;

    // If in edit mode and onDeleteOptionClick is provided, open delete modal
    if (customField && onDeleteOptionClick) {
      const columnToDelete = dropdownOptions?.find(
        (column) => column?.id === id,
      );
      if (columnToDelete && columnToDelete.value) {
        onDeleteOptionClick(columnToDelete.value);
        return;
      }
    }

    // Otherwise, directly remove the column (create mode or no delete handler)
    this.setState((prevState) => {
      const updatedColumns = prevState.dropdownOptions?.filter(
        (column) => column?.id !== id,
      );
      const fieldErrors = this.computeDuplicateErrors(updatedColumns);
      return {
        dropdownOptions: updatedColumns,
        fieldErrors,
        activeColorPickerId:
          prevState.activeColorPickerId === id
            ? null
            : prevState.activeColorPickerId,
      };
    });
  }

  handleColumnChange(id: string, value: string) {
    this.setState((prevState) => {
      const updatedColumns = prevState.dropdownOptions?.map((column) =>
        column?.id === id
          ? {
              ...column,
              value,
            }
          : column,
      );

      const fieldErrors = this.computeDuplicateErrors(updatedColumns, id);

      return {
        dropdownOptions: updatedColumns,
        fieldErrors,
      };
    });
  }

  onDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!active || !over || active.id === over.id) return;

    this.setState((prevState) => {
      const current = prevState.dropdownOptions;
      const from = current.findIndex(
        (column) => column.id === String(active.id),
      );
      const to = current.findIndex((column) => column.id === String(over.id));

      if (from === -1 || to === -1) {
        return {};
      }

      return {
        dropdownOptions: arrayMove(current, from, to),
      };
    });
  }

  handleToggleColorPicker(id: string) {
    this.setState((prevState) => ({
      activeColorPickerId: prevState.activeColorPickerId === id ? null : id,
    }));
  }

  handleColorChange(id: string, color: PaletteColor) {
    this.setState((prevState) => {
      const updatedColumns = prevState.dropdownOptions.map((column) =>
        column.id === id
          ? {
              ...column,
              color: color.fill,
              borderColor: color.border,
            }
          : column,
      );
      const fieldErrors = this.computeDuplicateErrors(updatedColumns);
      return {
        dropdownOptions: updatedColumns,
        fieldErrors,
        activeColorPickerId: null,
      };
    });
  }

  onInputChange(value, e) {
    const { name } = e.target;
    this.setState(
      produce((draft) => {
        draft.values[name] = value;
        draft.dirty[name] =
          name === 'fieldName' ? value.length > 100 : value.length > 255;
      }),
    );
  }

  onTextAreaChange(e) {
    const { value } = e.target;
    const regex = /[{}]/g;
    const hasError = regex.test(value || '');
    const name = 'fallbackText';

    this.setState(
      produce((draft) => {
        draft.values[name] = value;
        draft.dirty[name] = value.length > 10000;
        draft.isFallbackHasError = hasError;
      }),
    );
  }

  onSelectChange(option) {
    const { values, errors, dirty, dropdownOptions, currencyCode } = this.state;
    const { fallbackText } = values;
    const maxFallbackLength = option.name === FieldType.LongText ? 10000 : 255;
    const isFallbackDirty = fallbackText.length > maxFallbackLength;
    const fallbackError = isFallbackDirty
      ? validate('fallbackText', fallbackText)
      : '';

    // If switching to Dropdown and no options exist, initialize with one empty option
    let newDropdownOptions = dropdownOptions;
    if (option.name === FieldType.Dropdown && dropdownOptions.length === 0) {
      newDropdownOptions = [this.createColumn('')];
    }

    let newCurrencyCode = currencyCode;
    if (option.name === FieldType.Currency && !currencyCode) {
      newCurrencyCode = currencyOptions[0]?.key;
    }

    this.setState({
      values: { ...values },
      fieldType: option.name,
      dirty: { ...dirty, fallbackText: isFallbackDirty },
      errors: { ...errors, fallbackText: fallbackError },
      dropdownOptions: newDropdownOptions,
      currencyCode: newCurrencyCode,
    });
  }

  onCurrencyChange = (option) => {
    this.setState({ currencyCode: option.key });
  };

  // eslint-disable-next-line class-methods-use-this
  createColumnId() {
    return `dropdown-option-${Date.now()}-${Math.random()
      .toString(16)
      .slice(2)}`;
  }

  createColumn(value = '', paletteColor?: PaletteColor): ColumnItem {
    const resolvedPalette = paletteColor ?? DEFAULT_FALLBACK_COLOR;
    return {
      id: this.createColumnId(),
      value,
      color: resolvedPalette.fill,
      borderColor: resolvedPalette.border,
      isHidden: false,
      isNew: false,
    };
  }

  onInputBlur(e) {
    const { name } = e.target;
    this.setState(
      produce((draft) => {
        if (draft.dirty[name]) {
          draft.errors[name] = validate(name, draft.values[name]);
        }
      }),
    );
  }

  onSubmit(e) {
    e.preventDefault();
    const { dirty, errors, values } = this.state;
    const dirtyKeys = Object.keys(dirty);

    const tempDirtyKeys = { ...dirty };
    const tempErrors = { ...errors };

    for (const key of dirtyKeys) {
      tempDirtyKeys[key] = true;
    }

    const keys = Object.keys(errors);
    let isError = false;

    for (const key of keys) {
      const error = validate(key, values[key]);
      tempErrors[key] = error;
      isError = isError || !!error;
    }

    if (isError) {
      this.setState({ errors: tempErrors, dirty: tempDirtyKeys });
      return;
    }

    const { fieldType, currencyCode, dropdownOptions } = this.state;
    const { onSave } = this.props;

    // Convert dropdownOptions back to string array
    const options = this.convertColumnItemsToOptions(dropdownOptions);

    const payload: CreateUpdateModalData = {
      label: values.fieldName,
      fallbackText: values.fallbackText,
      type: fieldType,
      currencyCode,
      options,
    };

    onSave(payload);
  }

  render() {
    const {
      values,
      errors,
      isDefault,
      dirty,
      fieldType,
      currencyCode,
      isFallbackHasError,
      dropdownOptions,
      fieldErrors,
      activeColorPickerId,
    } = this.state;
    const {
      show,
      modalHeaderTitle,
      onClose,
      isRequestPending,
      t,
      disableDropdownBox,
      customField,
    } = this.props;

    const isUnexpectedFieldType = !Object.values(FieldType).includes(
      fieldType as FieldType,
    );

    return (
      <Modal
        show={show}
        className="add-prospect-field"
        titleContent={
          <div className="modal-header-custom">
            <div className="sidenav__icon">
              <FileText width={24} height={24} color="#1D4ED8" />
            </div>
            <h4 className="ml-2">{modalHeaderTitle}</h4>
          </div>
        }
        onClose={onClose}
        onSubmit={this.onSubmit}
        showCloseIcon={false}
        cancelButtonText={t(`Cancel`)}
        submitButtonText={t(`Save`)}
        isSubmitLoading={isRequestPending}
        isSubmitDisabled={
          isRequestPending ||
          dirty.fieldName ||
          dirty.fallbackText ||
          isFallbackHasError ||
          Object.keys(fieldErrors || {}).length > 0 ||
          !values.fieldName.trim() ||
          (fieldType === FieldType.Dropdown &&
            dropdownOptions.every((opt) => !opt.value.trim())) ||
          isUnexpectedFieldType
        }
        backdrop="static"
        extraModalProps={{
          centered: true,
        }}
      >
        <div className="prospect-modal-body">
          <Input
            disabled={isDefault}
            name="fieldName"
            label={t(`Field Name`)}
            placeholder="Enter Field Name"
            value={values.fieldName}
            size={Input.Size.Medium}
            variant={errors.fieldName && Input.Variant.Error}
            caption={errors.fieldName ? t(`messages.${errors.fieldName}`) : ''}
            onChange={this.onInputChange}
            onBlur={this.onInputBlur}
          />
          {dirty.fieldName && (
            <div>
              <span className="error-label">
                <img src={Images.AlertTriangleRed} alt="Alert" />
                {t('labels.prospect_label_error')}
              </span>
            </div>
          )}
          <span className="select-label semibold-11">{t(`Field Type`)}</span>{' '}
          <Select<DropdownCreateUpdateOption>
            options={options}
            selectedOptionKey={fieldType}
            optionFilterProp="name"
            optionRenderer={(option) => (
              <span>
                {option?.key === FieldType.LongText
                  ? 'Long Text'
                  : capitalize(option?.name)}
              </span>
            )}
            showOptionsSeparator={true}
            onChange={([option]) => {
              this.onSelectChange(option);
            }}
            selectedOptionRenderer={([option]) => (
              <span>
                {option?.key === FieldType.LongText
                  ? 'Long Text'
                  : capitalize(option?.name)}
              </span>
            )}
            disabled={disableDropdownBox}
            className="field-type-select"
          />
          {/* Dropdown options */}
          {fieldType === FieldType.Dropdown && dropdownOptions && (
            <div className="categorize-by-modal__content custom-field-modal">
              <div className="categorize-by-modal__list">
                <DndContext
                  modifiers={[restrictToVerticalAxis]}
                  onDragEnd={this.onDragEnd}
                >
                  <SortableContext
                    items={dropdownOptions.map((column) => column.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {dropdownOptions.map((column, index) => {
                      const { newlyAddedColumnId } = this.state;
                      return (
                        <SortableColumnField
                          key={column.id}
                          column={column}
                          onChange={this.handleColumnChange}
                          onRemove={this.handleRemoveColumn}
                          disableRemove={
                            dropdownOptions.length === 1 && index === 0
                          }
                          isDuplicate={Boolean(fieldErrors[column.id])}
                          isColorSelectionEnabled={false}
                          onToggleColorPicker={this.handleToggleColorPicker}
                          onColorChange={this.handleColorChange}
                          isColorPickerOpen={activeColorPickerId === column.id}
                          availableColors={PRESET_COLORS}
                          errorMessage={fieldErrors[column.id]}
                          isHidden={Boolean(column.isHidden)}
                          isVisibilityToggleEnabled={false}
                          isDragDisabled={Boolean(column.isHidden)}
                          isNew={Boolean(column.isNew)}
                          showColorSwatch={false}
                          removeIcon={
                            customField ? (
                              <Trash width={20} height={20} />
                            ) : (
                              <Cross />
                            )
                          }
                          removeTooltip={customField ? 'Delete' : 'Remove'}
                          onAddNewOption={this.handleAddColumn}
                          shouldFocusOnMount={newlyAddedColumnId === column.id}
                        />
                      );
                    })}
                  </SortableContext>
                </DndContext>
              </div>

              <button
                type="button"
                className="categorize-by-modal__add-row d-flex gap-8"
              >
                <span className="categorize-by-modal__drag-handle categorize-by-modal__drag-handle--disabled">
                  <DotsVerticals />
                </span>
                <span
                  className="categorize-by-modal__add-label"
                  {...accessibleOnClick(this.handleAddColumn)}
                >
                  <Plus />
                  Add Option
                </span>
              </button>
            </div>
          )}
          {fieldType === FieldType.LongText ? (
            <div className="longtext-wrapper">
              <div className="longtext-label">
                <InputLabel
                  text="Fallback Text (Optional)"
                  place={InputLabel.Place.Before}
                />
                <OverlayTooltip
                  rootClose={true}
                  text={t('messages.fallback_text_info')}
                  className="tooltip-md"
                  placement={Placement.Bottom}
                >
                  <InfoCircle width={16} height={16} color="#6b7280" />
                </OverlayTooltip>
              </div>
              <textarea
                name="fallbackText"
                placeholder="Enter Fallback Text"
                value={values.fallbackText}
                onChange={this.onTextAreaChange}
                onBlur={this.onInputBlur}
                className="longtext-textarea"
                rows={3}
              />
              <div>
                {dirty.fallbackText && (
                  <span className="error-label">
                    <img src={Images.AlertTriangleRed} alt="Alert" />
                    Fallback text must be shorter than or equal to 10000
                    characters.
                  </span>
                )}
                {isFallbackHasError && (
                  <span className="error-label">
                    <img src={Images.AlertTriangleRed} alt="Alert" />
                    curly braces are not allowed.
                  </span>
                )}
              </div>
            </div>
          ) : (
            <>
              {fieldType !== FieldType.Date &&
                fieldType !== FieldType.Currency &&
                fieldType !== FieldType.Dropdown && ( // Show fallback input only if fieldType is not Date or Currency
                  <>
                    <Input
                      className="fallback-text-input"
                      name="fallbackText"
                      label={
                        <div className="d-flex gap-4">
                          <span>{t(`Fallback Text (Optional)`)}</span>
                          <OverlayTooltip
                            rootClose={true}
                            text={t('messages.fallback_text_info')}
                            className="tooltip-md"
                            placement={Placement.Bottom}
                          >
                            <InfoCircle
                              width={16}
                              height={16}
                              color="#6b7280"
                            />
                          </OverlayTooltip>
                        </div>
                      }
                      placeholder="Enter Fallback Text"
                      value={values.fallbackText}
                      size={Input.Size.Medium}
                      variant={errors.fallbackText && Input.Variant.Error}
                      caption={
                        errors.fallbackText
                          ? t(`messages.${errors.fallbackText}`)
                          : ''
                      }
                      onChange={this.onInputChange}
                      onBlur={this.onInputBlur}
                    />
                    <div>
                      {dirty.fallbackText && (
                        <span className="error-label">
                          <img src={Images.AlertTriangleRed} alt="Alert" />
                          {t('labels.prospect_fallback_text_error')}
                        </span>
                      )}
                    </div>
                  </>
                )}
              {fieldType === FieldType.Currency && (
                <>
                  <div className="currency-select">
                    <span className="select-label semibold-11">
                      {t(`Currency`)}
                    </span>
                    <Select<CurrencyOption>
                      options={currencyOptions}
                      selectedOptionKey={
                        currencyCode || currencyOptions[0]?.key
                      }
                      optionRenderer={(option) => <span>{option.name}</span>}
                      onChange={([option]) => this.onCurrencyChange(option)}
                      selectedOptionRenderer={([option]) => (
                        <span>{option.name}</span>
                      )}
                      className="currency-select-options"
                    />
                  </div>
                </>
              )}
            </>
          )}
          <hr />
        </div>
      </Modal>
    );
  }
}

export default withTranslation()(CreateUpdateCustomFieldModal);
